<!-- RecipeCreate.svelte -->
<script>
  import { createEventDispatcher } from 'svelte';
  import { discountItems, addRecipe } from '../store.js';
  
  const dispatch = createEventDispatcher();
  
  // Initial empty recipe
  let newRecipe = {
    name: '',
    ingredients: [{ id: null, name: '', amount: '' }],
    instructions: '',
    prepTime: 30,
    difficulty: 'Lätt',
    category: 'Middag',
    portions: 4,
    nutritionInfo: {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0
    }
  };
  
  // Available difficulty levels
  const difficultyLevels = ['Lätt', 'Medel', 'Svår'];
  
  // Available meal categories
  const mealCategories = ['Frukost', 'Lunch', 'Middag', 'Efterrätt', 'Mellanmål'];
  
  function closeModal() {
    dispatch('close');
  }
  
  function addIngredient() {
    newRecipe.ingredients = [...newRecipe.ingredients, { id: null, name: '', amount: '' }];
  }
  
  function removeIngredient(index) {
    newRecipe.ingredients = newRecipe.ingredients.filter((_, i) => i !== index);
  }
  
  function handleSubmit() {
    // Validate the form
    if (!newRecipe.name || newRecipe.name.trim() === '') {
      alert('Vänligen ange ett receptnamn');
      return;
    }
    
    if (newRecipe.ingredients.some(ing => !ing.name || ing.name.trim() === '')) {
      alert('Alla ingredienser måste ha ett namn');
      return;
    }
    
    if (!newRecipe.instructions || newRecipe.instructions.trim() === '') {
      alert('Vänligen ange tillagningsinstruktioner');
      return;
    }
    
    // Link ingredients to discount items where possible
    const linkedIngredients = newRecipe.ingredients.map(ing => {
      const matchingItem = $discountItems.find(item => 
        item.name.toLowerCase().includes(ing.name.toLowerCase()) ||
        ing.name.toLowerCase().includes(item.name.toLowerCase())
      );
      
      return {
        ...ing,
        id: matchingItem ? matchingItem.id : null
      };
    });
    
    // Create the final recipe
    const finalRecipe = {
      ...newRecipe,
      ingredients: linkedIngredients
    };
    
    // Add it to the store
    addRecipe(finalRecipe);
    
    // Notify parent and close modal
    dispatch('created', finalRecipe);
  }
</script>

<div class="modal-backdrop" on:click={closeModal}>
  <div class="modal-content" on:click|stopPropagation>
    <button class="close-button" on:click={closeModal}>×</button>
    
    <h2>Skapa nytt recept</h2>
    
    <form on:submit|preventDefault={handleSubmit}>
      <div class="form-group">
        <label for="recipe-name">Receptnamn</label>
        <input 
          type="text" 
          id="recipe-name" 
          bind:value={newRecipe.name} 
          placeholder="Ange ett namn på receptet" 
          required
        />
      </div>
      
      <div class="form-section">
        <h3>Ingredienser</h3>
        {#each newRecipe.ingredients as ingredient, index}
          <div class="ingredient-row">
            <div class="ingredient-inputs">
              <input 
                type="text" 
                placeholder="Ingrediens" 
                bind:value={ingredient.name}
                required
              />
              <input 
                type="text" 
                placeholder="Mängd" 
                bind:value={ingredient.amount}
                required
              />
            </div>
            {#if index > 0}
              <button 
                type="button" 
                class="remove-ingredient-button" 
                on:click={() => removeIngredient(index)}
              >
                ×
              </button>
            {/if}
          </div>
        {/each}
        
        <button type="button" class="add-ingredient-button" on:click={addIngredient}>
          + Lägg till ingrediens
        </button>
      </div>
      
      <div class="form-group">
        <label for="recipe-instructions">Tillagning</label>
        <textarea 
          id="recipe-instructions" 
          bind:value={newRecipe.instructions} 
          placeholder="Beskriv hur maträtten ska tillagas" 
          rows="5" 
          required
        ></textarea>
      </div>
      
      <div class="form-row">
        <div class="form-group">
          <label for="recipe-prep-time">Tillagningstid (min)</label>
          <input 
            type="number" 
            id="recipe-prep-time" 
            bind:value={newRecipe.prepTime} 
            min="5" 
            max="240"
          />
        </div>
        
        <div class="form-group">
          <label for="recipe-portions">Antal portioner</label>
          <input 
            type="number" 
            id="recipe-portions" 
            bind:value={newRecipe.portions} 
            min="1" 
            max="20"
          />
        </div>
      </div>
      
      <div class="form-row">
        <div class="form-group">
          <label for="recipe-difficulty">Svårighetsgrad</label>
          <select id="recipe-difficulty" bind:value={newRecipe.difficulty}>
            {#each difficultyLevels as level}
              <option value={level}>{level}</option>
            {/each}
          </select>
        </div>
        
        <div class="form-group">
          <label for="recipe-category">Kategori</label>
          <select id="recipe-category" bind:value={newRecipe.category}>
            {#each mealCategories as category}
              <option value={category}>{category}</option>
            {/each}
          </select>
        </div>
      </div>
      
      <div class="form-section">
        <h3>Näringsvärde (per portion)</h3>
        <div class="nutrition-grid">
          <div class="form-group">
            <label for="nutrition-calories">Kalorier (kcal)</label>
            <input 
              type="number" 
              id="nutrition-calories" 
              bind:value={newRecipe.nutritionInfo.calories} 
              min="0"
            />
          </div>
          
          <div class="form-group">
            <label for="nutrition-protein">Protein (g)</label>
            <input 
              type="number" 
              id="nutrition-protein" 
              bind:value={newRecipe.nutritionInfo.protein} 
              min="0"
            />
          </div>
          
          <div class="form-group">
            <label for="nutrition-carbs">Kolhydrater (g)</label>
            <input 
              type="number" 
              id="nutrition-carbs" 
              bind:value={newRecipe.nutritionInfo.carbs} 
              min="0"
            />
          </div>
          
          <div class="form-group">
            <label for="nutrition-fat">Fett (g)</label>
            <input 
              type="number" 
              id="nutrition-fat" 
              bind:value={newRecipe.nutritionInfo.fat} 
              min="0"
            />
          </div>
        </div>
      </div>
      
      <div class="form-actions">
        <button type="button" class="cancel-button" on:click={closeModal}>Avbryt</button>
        <button type="submit" class="save-button">Spara recept</button>
      </div>
    </form>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 20px;
  }
  
  .modal-content {
    background-color: white;
    border-radius: 10px;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
    width: 100%;
    max-width: 800px;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    padding: 30px;
  }
  
  .close-button {
    position: absolute;
    top: 10px;
    right: 15px;
    background: none;
    border: none;
    font-size: 30px;
    cursor: pointer;
    color: #666;
  }
  
  h2 {
    margin-top: 0;
    margin-bottom: 20px;
    color: #333;
    font-size: 22px;
    border-bottom: 2px solid #e91e63;
    padding-bottom: 10px;
  }
  
  h3 {
    margin-top: 0;
    margin-bottom: 15px;
    color: #333;
    font-size: 18px;
  }
  
  .form-group {
    margin-bottom: 20px;
  }
  
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }
  
  .form-section {
    margin-bottom: 30px;
    padding: 20px;
    background-color: #f9f9f9;
    border-radius: 8px;
  }
  
  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #333;
  }
  
  input[type="text"],
  input[type="number"],
  textarea,
  select {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 14px;
    color: #333;
  }
  
  input[type="text"]:focus,
  input[type="number"]:focus,
  textarea:focus,
  select:focus {
    border-color: #e91e63;
    outline: none;
    box-shadow: 0 0 0 2px rgba(233, 30, 99, 0.2);
  }
  
  .ingredient-row {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  }
  
  .ingredient-inputs {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 10px;
    flex: 1;
  }
  
  .remove-ingredient-button {
    background: none;
    border: none;
    color: #ff6b6b;
    font-size: 24px;
    cursor: pointer;
    margin-left: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
  }
  
  .add-ingredient-button {
    background-color: #4caf50;
    color: white;
    border: none;
    padding: 8px 15px;
    border-radius: 5px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
    display: block;
    margin-top: 10px;
  }
  
  .add-ingredient-button:hover {
    background-color: #3f9142;
  }
  
  .nutrition-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 15px;
    margin-top: 30px;
  }
  
  .cancel-button {
    padding: 10px 20px;
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .cancel-button:hover {
    background-color: #e0e0e0;
  }
  
  .save-button {
    padding: 10px 20px;
    background-color: #e91e63;
    color: white;
    border: none;
    border-radius: 5px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .save-button:hover {
    background-color: #d81b60;
  }
  
  @media (max-width: 768px) {
    .form-row, 
    .nutrition-grid {
      grid-template-columns: 1fr;
    }
  }
</style>